import { buildPairIndex } from './method-stats.js';
/**
 * Project a session's captured messages onto three horizontal lanes for a
 * waterfall view:
 *
 * - `editor-req`  — requests sent from the editor to the agent
 *                   (most user traffic: initialize, prompt, set_mode, …)
 * - `agent-req`   — the rarer reverse direction (permissions etc)
 * - `notification` — any-direction notifications, including streaming chunks
 *
 * Responses and errors are NOT their own events — they extend the request
 * rect's `endTs` (and flip `erroredOut`). An unpaired request produces a
 * zero-width "tick" at its timestamp.
 *
 * Pure — no DOM, no time, no IO. Used by the canvas TimelineView in the UI;
 * could equally drive an SVG export or a TUI rendering.
 */
export function buildTimelineLayout(messages) {
    const empty = {
        events: [],
        startTs: 0,
        endTs: 0,
        durationMs: 0,
        laneCounts: { 'editor-req': 0, 'agent-req': 0, notification: 0 },
    };
    if (messages.length === 0)
        return empty;
    const pairs = buildPairIndex(messages);
    const bySeq = new Map();
    for (const m of messages)
        bySeq.set(m.seq, m);
    let startTs = Infinity;
    let endTs = -Infinity;
    const events = [];
    const laneCounts = {
        'editor-req': 0,
        'agent-req': 0,
        notification: 0,
    };
    for (const m of messages) {
        if (m.timestamp < startTs)
            startTs = m.timestamp;
        if (m.timestamp > endTs)
            endTs = m.timestamp;
        if (m.kind === 'request') {
            const lane = m.direction === 'editor-to-agent' ? 'editor-req' : 'agent-req';
            const pair = pairs.get(m.seq);
            const pairedMsg = pair ? bySeq.get(pair.pairSeq) : undefined;
            const erroredOut = pairedMsg?.kind === 'error';
            const evtEnd = pairedMsg ? pairedMsg.timestamp : m.timestamp;
            if (evtEnd > endTs)
                endTs = evtEnd;
            events.push({
                seq: m.seq,
                startTs: m.timestamp,
                endTs: evtEnd,
                lane,
                method: m.method ?? null,
                kind: m.kind,
                erroredOut,
                pairedSeq: pairedMsg ? pairedMsg.seq : null,
            });
            laneCounts[lane] += 1;
        }
        else if (m.kind === 'notification') {
            events.push({
                seq: m.seq,
                startTs: m.timestamp,
                endTs: m.timestamp,
                lane: 'notification',
                method: m.method ?? null,
                kind: m.kind,
                erroredOut: false,
                pairedSeq: null,
            });
            laneCounts.notification += 1;
        }
        // Responses/errors/unknowns contribute to time-range only — they're
        // already represented by their paired request's extended endTs.
    }
    if (!Number.isFinite(startTs) || !Number.isFinite(endTs))
        return empty;
    return {
        events,
        startTs,
        endTs,
        durationMs: Math.max(0, endTs - startTs),
        laneCounts,
    };
}
//# sourceMappingURL=timeline-layout.js.map